import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus, Search, Pin, Trash2, MessageSquare, X } from 'lucide-react';
import { cn, timeAgo } from '../../lib/utils';
import Button from '../ui/Button';

/**
 * Collapsible conversation history. Static column on desktop; slide-in drawer
 * on mobile (controlled by `open` / `onClose`).
 */
export default function ConversationSidebar({
  conversations = [],
  activeId,
  onSelect,
  onNew,
  onPin,
  onDelete,
  loading,
  open,
  onClose,
}) {
  const [q, setQ] = useState('');
  const filtered = conversations.filter(
    (c) =>
      c.title.toLowerCase().includes(q.toLowerCase()) ||
      c.preview.toLowerCase().includes(q.toLowerCase())
  );

  const Panel = (
    <div className="flex h-full w-full flex-col">
      <div className="flex items-center gap-2 p-3">
        <Button icon={Plus} className="flex-1" size="sm" onClick={onNew}>
          New chat
        </Button>
        <button
          onClick={onClose}
          className="rounded-lg p-2 text-content-subtle hover:bg-white/5 hover:text-content md:hidden"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="px-3 pb-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-subtle" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search chats"
            className="glass h-9 w-full rounded-lg pl-9 pr-3 text-sm placeholder:text-content-subtle focus-visible:ring-2 focus-visible:ring-brand-400"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto px-2 pb-3 no-scrollbar">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="shimmer mx-1 h-12 rounded-lg bg-white/[0.03]" />
          ))
        ) : filtered.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-content-subtle">No conversations yet.</p>
        ) : (
          filtered.map((c) => (
            <div
              key={c.id}
              className={cn(
                'group relative flex cursor-pointer items-start gap-2 rounded-lg px-3 py-2.5 transition-colors',
                activeId === c.id ? 'bg-brand-500/15' : 'hover:bg-white/5'
              )}
              onClick={() => onSelect(c.id)}
            >
              <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-content-subtle" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  {c.pinned && <Pin className="h-3 w-3 shrink-0 text-amber-300" />}
                  <p className="truncate text-sm font-medium">{c.title}</p>
                </div>
                <p className="truncate text-xs text-content-subtle">
                  {c.preview || 'No messages yet'} · {timeAgo(c.updatedAt)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPin(c);
                  }}
                  className="rounded p-1 text-content-subtle hover:bg-white/10 hover:text-content"
                  aria-label="Pin"
                >
                  <Pin className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(c);
                  }}
                  className="rounded p-1 text-content-subtle hover:bg-danger/15 hover:text-danger"
                  aria-label="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop column */}
      <div className="hidden w-72 shrink-0 rounded-2xl border border-border/60 bg-surface/40 md:block">
        {Panel}
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-black/50 md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
            />
            <motion.div
              className="glass-strong fixed inset-y-0 left-0 z-50 w-80 max-w-[85vw] md:hidden"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            >
              {Panel}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
